import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import { FaLink, FaProjectDiagram, FaBuilding, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import './ProjectAssignmentModal.css';

function ProjectAssignmentModal({ stakeholder, projects, onClose, onAssign }) {
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [loading, setLoading] = useState(false);
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [fetchingAssignments, setFetchingAssignments] = useState(true);

  useEffect(() => {
    // Fetch stakeholder's current project assignments
    const fetchAssignments = async () => {
      try {
        const response = await apiClient.get(`/v2/stakeholders/${stakeholder.id}/projects`);
        setAssignedProjects(response.data.map(p => p.id));
      } catch (error) {
        console.error('Error fetching stakeholder projects:', error);
        // If the endpoint doesn't exist, just continue without pre-selected projects
        setAssignedProjects([]);
      } finally {
        setFetchingAssignments(false);
      }
    };

    fetchAssignments();
  }, [stakeholder.id]);

  const handleAssign = async () => {
    if (!selectedProjectId) {
      alert('Please select a project');
      return;
    }

    setLoading(true);
    try {
      await onAssign(stakeholder.id, selectedProjectId);
      onClose();
    } catch (error) {
      console.error('Error in assignment:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter out already assigned projects
  const availableProjects = projects.filter(p => !assignedProjects.includes(p.id));

  return (
    <div className="modal-overlay">
      <div className="project-assignment-modal">
        <div className="modal-header">
          <h3 className="modal-title">
            <FaLink className="modal-icon" />
            Assign {stakeholder.display_name || stakeholder.full_name} to Project
          </h3>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {/* Stakeholder Info Card */}
          <div className="stakeholder-info-card">
            <div className="info-header">
              <div className="info-icon">
                {stakeholder.stakeholder_category === 'internal' ? 
                  <FaBuilding /> : <FaExclamationCircle />
                }
              </div>
              <div className="info-details">
                <h4>{stakeholder.display_name || stakeholder.full_name}</h4>
                <p className="info-subtitle">{stakeholder.position || 'No position'}</p>
                <p className="info-meta">
                  {stakeholder.organization || 'No organization'} • {stakeholder.email}
                </p>
              </div>
            </div>
          </div>

          {/* Current Assignments */}
          {assignedProjects.length > 0 && (
            <div className="current-assignments">
              <h4 className="section-title">
                <FaCheckCircle className="section-icon success" />
                Currently Assigned Projects
              </h4>
              <div className="assigned-projects-list">
                {projects.filter(p => assignedProjects.includes(p.id)).map(project => (
                  <div key={project.id} className="assigned-project-item">
                    <FaProjectDiagram className="project-icon" />
                    <span>{project.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Project Selection */}
          <div className="project-selection">
            <h4 className="section-title">
              <FaProjectDiagram className="section-icon" />
              Select Project to Assign
            </h4>
            
            {fetchingAssignments ? (
              <div className="loading-state">Loading projects...</div>
            ) : availableProjects.length === 0 ? (
              <div className="empty-state">
                <FaCheckCircle className="empty-icon" />
                <p>This stakeholder is already assigned to all available projects.</p>
              </div>
            ) : (
              <div className="projects-grid">
                {availableProjects.map(project => (
                  <div 
                    key={project.id}
                    className={`project-card ${selectedProjectId === project.id ? 'selected' : ''}`}
                    onClick={() => setSelectedProjectId(project.id)}
                  >
                    <div className="project-card-header">
                      <FaProjectDiagram className="project-card-icon" />
                      <h5>{project.name}</h5>
                    </div>
                    {project.description && (
                      <p className="project-description">{project.description}</p>
                    )}
                    <div className="project-meta">
                      <span className="meta-item">Status: {project.status || 'Active'}</span>
                      {project.start_date && (
                        <span className="meta-item">
                          Started: {new Date(project.start_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button 
            className="btn secondary" 
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className="btn primary"
            onClick={handleAssign}
            disabled={loading || !selectedProjectId || availableProjects.length === 0}
          >
            {loading ? 'Assigning...' : 'Assign to Project'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProjectAssignmentModal;